/** Small shared pieces: pills, spinners, and the empty and error states. */

import type { ReactNode } from 'react'

import type { ItemPriority, ItemStatus } from '@/api/types'
import { PRIORITY_LABEL, STATUS_LABEL } from '@/lib/format'

// --- priority ---------------------------------------------------------------

/** Background and text for each priority. Kept as full class strings rather
 *  than interpolated fragments, because Tailwind scans source text literally
 *  and would not emit a class it never sees written out. */
const PRIORITY_PILL: Record<ItemPriority, string> = {
  imminent: 'bg-crimson-soft text-crimson',
  high: 'bg-warning-light text-warning-text',
  medium: 'bg-info-light text-info-text',
  low: 'bg-gray-200 text-gray-700',
}

const PRIORITY_BAR: Record<ItemPriority, string> = {
  imminent: 'bg-priority-imminent',
  high: 'bg-priority-high',
  medium: 'bg-priority-medium',
  low: 'bg-priority-low',
}

export function PriorityPill({ priority }: { priority: ItemPriority }) {
  return <span className={`pill ${PRIORITY_PILL[priority]}`}>{PRIORITY_LABEL[priority]}</span>
}

/** The colour stripe down the left edge of a row. Carries the priority at a
 *  glance without spending horizontal space, which matters on a phone. */
export function PriorityBar({ priority }: { priority: ItemPriority }) {
  return (
    <span
      aria-hidden
      className={`absolute inset-y-0 left-0 w-1 rounded-l-md ${PRIORITY_BAR[priority]}`}
    />
  )
}

// --- status -----------------------------------------------------------------

const STATUS_PILL: Record<ItemStatus, string> = {
  not_started: 'bg-gray-200 text-gray-700',
  in_progress: 'bg-info-light text-info-text',
  blocked: 'bg-warning-light text-warning-text',
  done: 'bg-success-light text-success-text',
  dropped: 'bg-gray-200 text-gray-500 line-through',
}

export function StatusPill({ status }: { status: ItemStatus }) {
  return <span className={`pill ${STATUS_PILL[status]}`}>{STATUS_LABEL[status]}</span>
}

// --- misc -------------------------------------------------------------------

/**
 * Styling for a pill sitting on a coloured row.
 *
 * Built from the row's own text colour at low alpha rather than from a second
 * palette, so one pill style works on every category colour without anyone
 * choosing a variant per hue.
 */
export function onTintPill(text: string) {
  return {
    color: text,
    backgroundColor: `${text}1f`,
    borderColor: `${text}4d`,
  }
}

export function ChecklistProgress({
  done,
  total,
  tintText,
}: {
  done: number
  total: number
  tintText?: string
}) {
  if (total === 0) return null
  const complete = done === total
  if (tintText) {
    return (
      <span
        className="pill border"
        style={onTintPill(tintText)}
        title={`${done} of ${total} checklist items complete`}
      >
        {done}/{total}
      </span>
    )
  }
  return (
    <span
      className={`pill ${complete ? 'bg-success-light text-success-text' : 'bg-surface-3 text-ink-3'}`}
      title={`${done} of ${total} checklist items complete`}
    >
      {done}/{total}
    </span>
  )
}

/**
 * The user's own free-text status.
 *
 * Renders nothing at all when unset -- not an empty pill, not a placeholder.
 * That is the whole contract: no status means no status.
 *
 * Styled distinctly from the workflow-status pill (a bordered, quoted note
 * rather than a filled badge) so the two are never mistaken for each other on
 * a row that shows both.
 */
export function StatusNote({
  note,
  className = '',
  tintText,
}: {
  note?: string | null
  className?: string
  tintText?: string
}) {
  if (!note) return null
  if (tintText) {
    return (
      <span className={`pill border ${className}`} style={onTintPill(tintText)} title={note}>
        {note}
      </span>
    )
  }
  return (
    <span
      className={`pill border border-navy-soft bg-navy-softer text-navy ${className}`}
      title={note}
    >
      {note}
    </span>
  )
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-ink-3" role="status">
      <span
        aria-hidden
        className="size-4 animate-spin rounded-full border-2 border-line-strong border-t-navy"
      />
      <span className="text-sm">{label}</span>
    </span>
  )
}

export function LoadingBlock({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center py-16">
      <Spinner label={label} />
    </div>
  )
}

/**
 * The empty state.
 *
 * An empty dashboard is the first thing this app ever shows, so it is designed
 * rather than left blank: it says what the space is for and offers the action
 * that fills it.
 */
export function EmptyState({
  title,
  children,
  action,
}: {
  title: string
  children?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <div
        aria-hidden
        className="flex size-12 items-center justify-center rounded-full bg-navy-soft text-2xl text-navy"
      >
        &#10003;
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      {children && <p className="max-w-sm text-sm text-ink-3">{children}</p>}
      {action}
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <p className="max-w-md text-sm text-error-text">{message}</p>
      {onRetry && (
        <button type="button" className="btn btn-neutral btn-sm" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  )
}

/** "Teaching &rsaquo; ACC 525" */
export function Breadcrumb({ category, subcategory }: { category: string; subcategory: string }) {
  return (
    <span className="truncate text-xs text-ink-3">
      {category} <span aria-hidden>&rsaquo;</span> <span className="text-ink-2">{subcategory}</span>
    </span>
  )
}

// --- forms ------------------------------------------------------------------

/**
 * A labelled control: the label above the box, always.
 *
 * Not the same as writing `<label>Client<select className="field" /></label>`.
 * A `<label>` is `display: inline` and `.field` is `width: 100%` with a
 * `max-w-*` cap, so inside a flex row the text sits *beside* the box whenever
 * the capped box leaves room on the line and *above* it when it does not. The
 * result is a form where some labels are above their control and some beside
 * it, decided by each control's width rather than by anyone's intent. Making
 * the label a flex column settles it for good.
 *
 * Width goes on the Field, not on the control: the control fills its label, so
 * one class decides how wide the column is and the two can never disagree.
 */
export function Field({
  label,
  hint,
  className = '',
  children,
}: {
  label: string
  hint?: string
  className?: string
  children: ReactNode
}) {
  return (
    <label className={`flex min-w-0 flex-col gap-1 ${className}`}>
      <span className="text-xs font-semibold text-ink-2">{label}</span>
      {children}
      {hint && <span className="text-xs text-ink-3">{hint}</span>}
    </label>
  )
}
